import path from 'path';

import axios from 'axios';
import { Atom, atom, getDefaultStore } from 'jotai';
import { atomWithStorage, createJSONStorage } from 'jotai/utils';

import { login } from '~constants';
import { OidcTokens } from '~features/auth/interfaces/oidcTokens';
import {
	parseAuthenticationResultToOidcTokens,
	parseTokenResponseTypeToOidcTokens,
	TokenResponseType,
} from '~features/auth/interfaces/tokenResponseType';
import User from '~interfaces/user';

const oidcTokensStorage = createJSONStorage<OidcTokens | null>(() => localStorage);
const oidcTokensAtom = atomWithStorage<OidcTokens | null>(`oidc.tokens`, null, oidcTokensStorage, {
	getOnInit: true,
});

const connectUserStorage = createJSONStorage<User | null>(() => localStorage);
const connectUserAtom = atomWithStorage<User | null>('user', null, connectUserStorage, {
	getOnInit: true,
});

const isAuthLoadingAtom = atom<{ isLoading: boolean; bypass: boolean }>({
	isLoading: false,
	bypass: false,
});

export interface Session {
	user: User | null;
	accessToken?: string;
	refreshToken?: string;
	isExpired: boolean;
	expiresIn?: number;
	expiresAt?: number;
	isOtpFlow?: boolean;
	hasValidSession: boolean;
	isLoading: boolean;
	bypassLoader: boolean;
}

const sessionAtom: Atom<Promise<Session>> = atom(async (get) => {
	let oidcTokens = get(oidcTokensAtom);
	const connectUser = get(connectUserAtom);
	const loadingState = get(isAuthLoadingAtom);

	const now = Math.floor(Date.now() / 1000);
	const isExpired = !oidcTokens || !oidcTokens.expiresAt || oidcTokens.expiresAt <= now;

	const session = {
		user: connectUser,
		accessToken: oidcTokens?.accessToken,
		refreshToken: oidcTokens?.refreshToken,
		isExpired: isExpired,
		expiresIn: oidcTokens?.expiresIn,
		expiresAt: oidcTokens?.expiresAt,
		isOtpFlow: oidcTokens?.isOtpFlow,
		hasValidSession: oidcTokens != null && !isExpired,
		isLoading: loadingState.isLoading,
		bypassLoader: loadingState.bypass,
	};

	// if it's expired and we have an active account, attempt a refresh tokens
	if (isExpired && oidcTokens?.canRefresh && !refreshPromise) {
		refreshPromise = sessionStore.refreshTokens(session);
	}

	if (refreshPromise) {
		try {
			await refreshPromise;
			oidcTokens = get(oidcTokensAtom);
			return {
				...session,
				accessToken: oidcTokens?.accessToken,
				refreshToken: oidcTokens?.refreshToken,
				expiresIn: oidcTokens?.expiresIn,
				expiresAt: oidcTokens?.expiresAt,
				hasValidSession: oidcTokens != null,
				isOtpFlow: oidcTokens?.isOtpFlow,
			};
		} catch (_) {
			return {
				user: connectUser,
				accessToken: oidcTokens?.accessToken,
				refreshToken: oidcTokens?.refreshToken,
				isExpired: isExpired,
				expiresIn: oidcTokens?.expiresIn,
				expiresAt: oidcTokens?.expiresAt,
				isOtpFlow: oidcTokens?.isOtpFlow,
				hasValidSession: false,
				isLoading: false,
				bypassLoader: loadingState.bypass,
			};
		}
	}

	return session;
});

const store = getDefaultStore();

let refreshPromise: Promise<OidcTokens> | null = null;

export const sessionStore = {
	setOidcTokens(oidcTokens: OidcTokens | null) {
		store.set(oidcTokensAtom, oidcTokens);
	},

	setLoadingUser(isLoading: boolean, bypass: boolean) {
		store.set(isAuthLoadingAtom, {
			isLoading,
			bypass,
		});
	},

	setConnectUser(user: User | null) {
		store.set(connectUserAtom, user);
	},

	clear() {
		store.set(oidcTokensAtom, null);
		store.set(connectUserAtom, null);
		login.msalInstance.clearCache();
	},
	async refreshTokens(session: Session): Promise<OidcTokens> {
		console.info('[Auth] Attempting silent token refresh', refreshPromise);
		sessionStore.setLoadingUser(true, false);

		if (!refreshPromise) {
			refreshPromise = session.isOtpFlow ? refreshOtpSession(session) : refreshSsoSession();
		}

		console.info('[Auth] Attempting silent token refresh:', refreshPromise);
		return await refreshPromise
			.then(async (newTokens: OidcTokens) => {
				sessionStore.setLoadingUser(false, false);
				sessionStore.setOidcTokens(newTokens);

				const { data: user } = await axios.get<User>(login.urlAuthToken, {
					baseURL: path.join(import.meta.env.BASE_URL, 'internal', 'users', 'me'),
					headers: {
						'Content-Type': 'application/json',
						Authorization: `Bearer ${newTokens.accessToken}`,
					},
				});
				sessionStore.setConnectUser(user);
				return Promise.resolve(newTokens);
			})
			.catch((refreshError) => {
				sessionStore.setLoadingUser(false, false);
				console.error('[Auth] Refresh failed', refreshError);
				// Clear session and redirect
				sessionStore.clear();
				return Promise.reject(refreshError);
			})
			.finally(() => (refreshPromise = null));
	},

	async getSnapshot(): Promise<Session> {
		return await store.get(sessionAtom);
	},
};

const refreshOtpSession = async (session: Session): Promise<OidcTokens> => {
	if (!session.refreshToken) throw new Error('No refresh token');

	const body = {
		client_id: import.meta.env.VITE_SKOPEI_OIDC_CLIENT_ID,
		scope: login.signInScopes,
		refresh_token: session.refreshToken,
		grant_type: 'refresh_token',
	};

	try {
		const { data } = await axios.post<TokenResponseType>(login.urlAuthToken, body, {
			baseURL: import.meta.env.VITE_SKOPEI_OIDC_AUTHORITY_URL_V2,
			headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
		});
		return Promise.resolve(parseTokenResponseTypeToOidcTokens(data));
	} catch (error) {
		return Promise.reject(error);
	}
};

const refreshSsoSession = async (): Promise<OidcTokens> => {
	await login.msalInstance.initialize();
	const activeAccount = login.msalInstance.getAllAccounts()[0];
	if (!activeAccount) throw new Error('No active account');

	try {
		const tokens = await login.msalInstance.acquireTokenSilent({
			account: activeAccount,
			redirectUri: window.location.origin + '/auth/silent',
			scopes: [...login.signInScopesArray],
		});
		return Promise.resolve(parseAuthenticationResultToOidcTokens(tokens));
	} catch (error) {
		return Promise.reject(error);
	}
};

export default sessionAtom;
